import React, { useEffect, useState, useRef } from "react";
import Header from "../components/Layout/Header";
import Footer from "../components/Layout/Footer";
import AxiosInstance from "../utils/axiosInstance";
import toast from "react-hot-toast";
import ALDOTLogo from "../assets/ALDOT-Dark.jpg";

const EconomicImpactForm = () => {
  const [companySuggestions, setCompanySuggestions] = useState([]);
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false); // New state for success view

  const initialFormState = {
    company_name: "",
    contract_date: "",
    total_employees: "",
    local_hires: "",
    meal_expenses: "",
    lodging_expenses: "",
    fuel_expenses: "",
  };

  const [formData, setFormData] = useState(initialFormState);
  const [errors, setErrors] = useState({});
  const suggestionRef = useRef(null);

  useEffect(() => {
    fetchCompanies();

    const handleClickOutside = (event) => {
      if (
        suggestionRef.current &&
        !suggestionRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchCompanies = async () => {
    const stored = localStorage.getItem("companies");
    if (stored) {
      setCompanySuggestions(JSON.parse(stored));
      return;
    }

    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const endpoint = process.env.REACT_APP_GET_COMPANIES;
      const res = await AxiosInstance.get(`${apiUrl}${endpoint}`);

      if (res?.data?.data) {
        const names = res.data.data.map((item) => item.name);
        setCompanySuggestions(names);
        localStorage.setItem("companies", JSON.stringify(names));
      }
    } catch (err) {
      console.error("Failed to fetch company names:", err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    let newValue = value;

    // ✅ force integer for these fields
    if (["total_employees", "local_hires"].includes(name)) {
      newValue = value.replace(/[^0-9]/g, ""); // remove decimals & letters
    }

    if (name === "company_name") {
      const filtered = companySuggestions.filter((name) =>
        name.toLowerCase().includes(value.toLowerCase()),
      );
      setFilteredSuggestions(filtered);
      setShowSuggestions(true);
    }

    setFormData((prev) => ({ ...prev, [name]: newValue }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSuggestionClick = (name) => {
    setFormData((prev) => ({ ...prev, company_name: name }));
    setShowSuggestions(false);
  };

  const validate = () => {
    let newErrors = {};
    if (!formData.company_name.trim())
      newErrors.company_name = "Company name is required";
    if (!formData.contract_date)
      newErrors.contract_date = "Date of contract is required";

    const total = parseInt(formData.total_employees || 0);
    const local = parseInt(formData.local_hires || 0);
    if (!formData.total_employees || total <= 0) {
      newErrors.total_employees = "Total employees must be at least 1";
    }
    if (local > total) {
      newErrors.local_hires = "Local hires cannot exceed total employees";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    console.log("hello asd ");
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    try {
      const payload = {
        ...formData,
        total_employees: parseInt(formData.total_employees) || 0,
        local_hires: parseInt(formData.local_hires) || 0,
        meal_expenses: parseFloat(formData.meal_expenses) || 0,
        lodging_expenses: parseFloat(formData.lodging_expenses) || 0,
        fuel_expenses: parseFloat(formData.fuel_expenses) || 0,
      };
      const endPoint = process.env.REACT_APP_EconomicFormSubmit || "";
      const response = await AxiosInstance.post(
        `${process.env.REACT_APP_API_URL}${endPoint}`,
        payload,
      );

      if (response.status === 200 || response.status === 201) {
        toast.success("Economic Impact Report submitted successfully!");
        setSubmitted(true); // Toggle success view
        setFormData(initialFormState);
      } else {
        toast.success("Economic Impact Report submitted successfully!");
      }
    } catch (err) {
      //   console.error("Submission error:", err);

      toast.error(
        err.response?.data?.message ||
          err.response?.message ||
          "Failed to submit the report.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Internal component for the success message
  const SuccessView = () => (
    <div className="text-center py-5">
      <div className="mb-4">
        <i
          className="bi bi-check-circle-fill text-success"
          style={{ fontSize: "5rem" }}
        ></i>
      </div>
      <h1 className="fw-bold text-dark display-5">Thank You!</h1>
      <p className="lead text-muted mb-4">Your submission has been received.</p>
      <button
        type="button"
        className="btn btn-primary px-5 py-2 rounded-0 text-uppercase fw-bold"
        onClick={() => setSubmitted(false)}
      >
        Submit Another Entry
      </button>
    </div>
  );

  return (
    <>
      <style jsx>{`
        .aldot-responsive-logo {
          height: 50px;
        }
        @media (min-width: 768px) {
          .aldot-responsive-logo {
            height: 100px;
          }
        }
        @media (min-width: 1200px) {
          .aldot-responsive-logo {
            height: 120px;
          }
        }
        .suggestion-item:hover {
          background-color: #0d6efd !important;
          color: white !important;
        }
        .scroll-container::-webkit-scrollbar {
          width: 6px;
        }
        .scroll-container::-webkit-scrollbar-thumb {
          background: #ccc;
          border-radius: 3px;
        }
      `}</style>

      <div className="vh-100 d-flex flex-column bg-light">
        <Header />

        <div className="bg-primary py-4">
          <div className="container">
            <h1 className="text-white fw-bold mb-0">Economic Impact Form</h1>
            <p className="text-white-50 small mb-0">
              Track and report project contributions
            </p>
          </div>
        </div>

        <div className="flex-grow-1 py-5">
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-lg-10 col-xl-8 mb-4">
                <div className="d-flex align-items-center justify-content-between bg-white p-3 shadow-sm border-start border-4 border-primary">
                  <img
                    src={ALDOTLogo}
                    alt="ALDOT Logo"
                    className="aldot-responsive-logo"
                    style={{ width: "auto", objectFit: "contain" }}
                  />
                </div>
              </div>

              <div className="col-lg-10 col-xl-8">
                <div className="card border-0 shadow-sm rounded-0">
                  <div className="card-body p-4 p-md-5">
                    {!submitted ? (
                      <form onSubmit={handleSubmit}>
                        <div
                          className="mb-4 position-relative"
                          ref={suggestionRef}
                        >
                          <label className="form-label fw-bold text-primary">
                            Company Name
                          </label>
                          <input
                            type="text"
                            className={`form-control rounded-0 ${errors.company_name ? "is-invalid" : ""}`}
                            name="company_name"
                            placeholder="Search for a company..."
                            value={formData.company_name}
                            onChange={handleInputChange}
                            onFocus={() => setShowSuggestions(true)}
                            autoComplete="off"
                          />
                          {showSuggestions &&
                            filteredSuggestions.length > 0 && (
                              <ul
                                className="list-group position-absolute w-100 z-3 shadow-sm rounded-0 scroll-container"
                                style={{
                                  maxHeight: "200px",
                                  overflowY: "auto",
                                }}
                              >
                                {filteredSuggestions.map(
                                  (suggestion, index) => (
                                    <button
                                      type="button"
                                      key={index}
                                      className="list-group-item list-group-item-action suggestion-item text-start"
                                      onClick={() =>
                                        handleSuggestionClick(suggestion)
                                      }
                                    >
                                      {suggestion}
                                    </button>
                                  ),
                                )}
                              </ul>
                            )}
                          {errors.company_name && (
                            <div className="invalid-feedback">
                              {errors.company_name}
                            </div>
                          )}
                        </div>

                        <div className="row g-3">
                          <div className="col-md-6 mb-3">
                            <label className="form-label fw-bold text-primary">
                              Date of Contract
                            </label>
                            <input
                              type="date"
                              className={`form-control rounded-0 ${errors.contract_date ? "is-invalid" : ""}`}
                              name="contract_date"
                              value={formData.contract_date}
                              onChange={handleInputChange}
                            />
                            {errors.contract_date && (
                              <div className="invalid-feedback">
                                {errors.contract_date}
                              </div>
                            )}
                          </div>
                          <div className="col-md-6 mb-3">
                            <label className="form-label fw-bold text-primary">
                              Total Employees
                            </label>
                            <input
                              type="number"
                              className="form-control rounded-0"
                              name="total_employees"
                              placeholder="0"
                              value={formData.total_employees}
                              onChange={handleInputChange}
                              min="0"
                              step="1" // ✅ only integers
                            />
                          </div>
                        </div>

                        <div className="mb-4">
                          <label className="form-label fw-bold text-primary">
                            Local Hires
                          </label>
                          <input
                            type="number"
                            className={`form-control rounded-0 ${errors.local_hires ? "is-invalid" : ""}`}
                            name="local_hires"
                            placeholder="Number of local hires"
                            value={formData.local_hires}
                            onChange={handleInputChange}
                            min="0"
                            step="1" // ✅ only integers
                          />
                          {errors.local_hires && (
                            <div className="invalid-feedback">
                              {errors.local_hires}
                            </div>
                          )}
                        </div>

                        <div className="p-4 bg-light border-start border-4 border-primary mb-4">
                          <h5 className="text-secondary fw-bold mb-3">
                            Project Expenses (USD)
                          </h5>
                          <div className="row g-3">
                            {[
                              "meal_expenses",
                              "lodging_expenses",
                              "fuel_expenses",
                            ].map((field) => (
                              <div className="col-md-4" key={field}>
                                <label className="form-label small fw-bold text-uppercase text-muted">
                                  {field.replace("_", " ")}
                                </label>
                                <div className="input-group">
                                  <span className="input-group-text bg-white border-end-0">
                                    $
                                  </span>
                                  <input
                                    type="number"
                                    step="0.01"
                                    className="form-control border-start-0 rounded-0"
                                    name={field}
                                    placeholder="0.00"
                                    value={formData[field]}
                                    onChange={handleInputChange}
                                    min="0"
                                  />
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className="d-flex gap-3">
                          <button
                            type="submit"
                            className="btn btn-primary px-5 py-2 rounded-0 text-uppercase fw-bold"
                            disabled={isSubmitting}
                          >
                            {isSubmitting ? "Submitting..." : "Submit Report"}
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline-secondary px-4 py-2 rounded-0 text-uppercase"
                            onClick={() => setFormData(initialFormState)}
                          >
                            Clear
                          </button>
                        </div>
                      </form>
                    ) : (
                      <SuccessView />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    </>
  );
};

export default EconomicImpactForm;
